import React, { ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../contexts/LocaleContext';
import { formatHardwarePrice } from '../../lib/hardwareFirst';
import * as types from '../../types';
import { ReactComponent as IconBell } from '../../assets/images/icons/icon_bell.svg';
import { ReactComponent as IconHeart } from '../../assets/images/icons/icon_heart.svg';
import { ReactComponent as IconLocation } from '../../assets/images/icons/icon_location.svg';
import { ReactComponent as IconMagic } from '../../assets/images/icons/icon_magic.svg';
import { ReactComponent as IconSatellite } from '../../assets/images/icons/icon_satellite.svg';
import { ReactComponent as IconVibration } from '../../assets/images/icons/icon_vibration.svg';
import { getFiGrowthBook } from '../../lib/growthbook';
import Button from '../Button';
import HardwareFirstPlanModal from '../HardwareFirstPlanModal/HardwareFirstPlanModal';
import HardwareFirstIntelligenceModal from './HardwareFirstIntelligenceModal';
import HardwareFirstUltraFeatureModal, { UltraFeature } from './HardwareFirstUltraFeatureModal';
import StarRating from '../StarRating/StarRating';
import { PickYourFiDeviceKey } from './HardwareFirstBelowFold';
import styles from './HardwareFirstSolo.module.scss';

/**
 * Boolean growthbook feature for the redesigned shop: one tracker renders as a product page, and the
 * full lineup takes the same design with its chooser on top.
 */
export const PDP_V2_FLAG = 'ecom-pdp-v2';

export function pdpV2Enabled(): boolean {
  return getFiGrowthBook().getFeatureValue(PDP_V2_FLAG, false);
}

/**
 * The tracker's price on one line: what it costs now, the list price struck when a code is on,
 * and a pill for either what the code took off or what the live promotion would.
 */
function SoloPrice({
  discount,
  offer,
}: {
  discount: types.HardwareDiscountPreview | undefined;
  offer: types.HardwareDiscountPreview | undefined;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  if (!discount) {
    return null;
  }
  const savedInCents = discount.listPriceInCents - discount.discountedPriceInCents;
  const showOffer = savedInCents <= 0 && !!offer && offer.promoDiscountInCents > 0;
  return (
    <div className={styles.price}>
      <span className={styles.priceAmount}>{formatHardwarePrice(discount.discountedPriceInCents, locale)}</span>
      {savedInCents > 0 && (
        <span className={styles.priceWas}>{formatHardwarePrice(discount.listPriceInCents, locale)}</span>
      )}
      {savedInCents > 0 && (
        <span className={styles.savedPill}>
          {t('hardware_first_card_saved', { amount: formatHardwarePrice(savedInCents, locale) })}
        </span>
      )}
      {showOffer && offer && (
        <span className={styles.offerPill}>
          {t('hardware_first_card_price_with_promo', {
            // a teaser, not the quote: whole dollars, the exact price lands once the code is on
            price: locale.wholeNumberCurrencyFormatter.format(Math.round(offer.discountedPriceInCents / 100)),
          })}
        </span>
      )}
    </div>
  );
}

/** The solo page's header: stars, the tracker's name, its price and its one-line pitch. */
export function SoloHeader({
  name,
  lede,
  discount,
  offer,
}: {
  name: string;
  lede: string;
  discount: types.HardwareDiscountPreview | undefined;
  offer: types.HardwareDiscountPreview | undefined;
}) {
  return (
    <div className={styles.header}>
      <div className={styles.starRow}>
        <StarRating />
      </div>
      <h1 className={styles.title}>{name}</h1>
      <SoloPrice discount={discount} offer={offer} />
      <p className={styles.lede}>{lede}</p>
    </div>
  );
}

/** The white box that holds s3+'s inline size and color pickers and the cta under them. */
export function SoloBuyBox({ children }: { children: ReactNode }) {
  return <div className={styles.buyBox}>{children}</div>;
}

/** The solo page's in-page cta; the pinned footer takes over when it scrolls away and on mobile. */
export function SoloCta({
  label,
  disabled,
  onClick,
  ctaRef,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  ctaRef: RefObject<HTMLDivElement>;
}) {
  return (
    <div className={styles.cta} ref={ctaRef}>
      <Button className={styles.ctaButton} disabled={disabled} onClick={onClick}>
        {label}
      </Button>
    </div>
  );
}

type Tile = {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  key: string;
  drawer?: 'intelligence' | UltraFeature;
};

// four feature tiles per tracker; ultra trades the health rows for its satellite and recall ones
const DOG_TILES: Tile[] = [
  { Icon: IconLocation, key: 'pdp_v2_tile_live_gps' },
  { Icon: IconBell, key: 'pdp_v2_tile_escape_alerts' },
  { Icon: IconHeart, key: 'pdp_v2_tile_activity_sleep' },
  { Icon: IconMagic, key: 'pdp_v2_tile_ai_health', drawer: 'intelligence' },
];
const ULTRA_TILES: Tile[] = [
  { Icon: IconLocation, key: 'pdp_v2_tile_live_gps' },
  { Icon: IconSatellite, key: 'pdp_v2_tile_satellite', drawer: 'starlink' },
  { Icon: IconBell, key: 'pdp_v2_tile_escape_alerts' },
  { Icon: IconVibration, key: 'pdp_v2_tile_sound_vibration', drawer: 'callback' },
];

// the prototype's battery: a green cell, mostly full
function BatteryIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 32 18" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="26" height="16" rx="4" stroke="#07a157" strokeWidth="2" />
      <rect x="4" y="4" width="18" height="10" rx="2" fill="#07a157" />
      <rect x="29" y="6" width="3" height="6" rx="1" fill="#07a157" />
    </svg>
  );
}

// ultra's five-day battery is a spec row, not a headline
const BATTERY_KEY: Partial<Record<PickYourFiDeviceKey, string>> = {
  mini: 'pdp_v2_battery_up_to_2_months',
  s3: 'pdp_v2_battery_up_to_3_months',
};

/** The battery headline and the four feature tiles under the solo header. */
export function SoloFeatures({ deviceKey }: { deviceKey: PickYourFiDeviceKey }) {
  const { t } = useTranslation();
  const batteryKey = BATTERY_KEY[deviceKey];
  const tiles = deviceKey === 'ultra' ? ULTRA_TILES : DOG_TILES;
  const learnMore = (
    <button className={styles.tileLearnMore} type="button">
      {t('learn_more')}
    </button>
  );
  return (
    <div className={styles.features}>
      {batteryKey && (
        <div className={styles.battery}>
          <BatteryIcon className={styles.batteryIcon} />
          <span>
            <strong className={styles.batteryHeadline}>{t(batteryKey)}</strong>
            <span className={styles.batterySub}>{t('pdp_v2_battery_on_a_single_charge')}</span>
          </span>
        </div>
      )}
      <ul className={styles.tiles}>
        {tiles.map(({ Icon, key, drawer }) => (
          <li key={key} className={styles.tile}>
            <span className={styles.tileIcon}>
              <Icon />
            </span>
            <span>
              {t(key)}
              {drawer === 'intelligence' && <HardwareFirstIntelligenceModal trigger={learnMore} />}
              {(drawer === 'starlink' || drawer === 'callback') && (
                <HardwareFirstUltraFeatureModal feature={drawer} trigger={learnMore} />
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The plan card the solo page shows: what the GPS/LTE plan does, with the plan modal behind `Learn more`. */
export function SoloPlanCard({ deviceName }: { deviceName: string }) {
  const { t } = useTranslation();
  return (
    <div className={styles.planCard}>
      <span className={styles.planIcon}>
        <IconLocation />
      </span>
      <div>
        <div className={styles.planTitle}>{t('pdp_v2_plan_title')}</div>
        {/* learn more closes the sentence, so the card holds to two lines */}
        <p className={styles.planBody}>
          {t('pdp_v2_plan_body')}{' '}
          <HardwareFirstPlanModal
            compact={{ deviceName }}
            trigger={
              <button className={styles.planLearnMore} type="button">
                {t('learn_more')}
              </button>
            }
          />
        </p>
      </div>
    </div>
  );
}

/** Shipping, returns and warranty as three compact tiles. */
export function SoloTrustRow() {
  const { t } = useTranslation();
  const tiles = [
    { title: t('free_shipping'), sub: t('pdp_v2_trust_shipping_sub') },
    { title: t('hardware_first_returns_title'), sub: t('pdp_v2_trust_returns_sub') },
    { title: t('one_year_warranty'), sub: t('cart_item_v2.warranty_description') },
  ];
  return (
    <ul className={styles.trust}>
      {tiles.map(({ title, sub }) => (
        <li key={title} className={styles.trustTile}>
          <span className={styles.trustTitle}>{title}</span>
          <span className={styles.trustSub}>{sub}</span>
        </li>
      ))}
    </ul>
  );
}
